import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import classNames from "classnames";
import dayjs, { Dayjs } from "dayjs";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BoardApi } from "../../../services/board-service-api";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaOverflowText from "../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import { boardKeys } from "./board-query-keys";
import { buildBoardPath } from "./board-routing";
import { BoardService } from "./board-service";
import { roadmapChanged } from "./change-event-routing";
import "./board-roadmap-view.scss";
import { Board, BoardChangeEvent, BoardStatus, Issue, IssueLink, Project, RoadmapRow } from "./types";
import { useBoardConfig } from "./use-board";

// Roadmap rows + their inter-epic links, fetched together and cached as one entry.
interface RoadmapData {
  rows: RoadmapRow[];
  links: IssueLink[];
}

type Zoom = "weeks" | "months" | "quarters";

export interface BoardRoadmapViewProps {
  board: Board;
  filter: string;
}

interface BarGeometry {
  rowIndex: number;
  left: number;
  width: number;
}

const ROW_HEIGHT: number = 48;

interface EpicRow {
  epic: Issue;
  start: Dayjs;
  end: Dayjs;
  childrenCount: number;
  completedCount: number;
}

const ZOOM_LABELS: Record<Zoom, string> = {
  weeks: "Weeks",
  months: "Months",
  quarters: "Quarters"
};

const BoardRoadmapView: React.FC<BoardRoadmapViewProps> = ({ board, filter }) => {
  const navigate = useNavigate();
  const { domainName } = useParams<{ domainName: string }>();
  const [zoom, setZoom] = useState<Zoom>("months");
  const showProject: boolean = board.scope.kind === "multi_project";
  const draggedRef = useRef<boolean>(false);
  // The timeline body, measured during epic drag (px → time-slot math) — a ref instead of a
  // closest() class lookup off the drag target.
  const timelineBodyRef = useRef<HTMLDivElement | null>(null);
  const { config } = useBoardConfig();
  const statuses: BoardStatus[] = config.statuses;
  const projects: Project[] = config.projects;

  // Roadmap rows (epics with computed dates + child/progress counts) and the inter-epic
  // dependency links are both sourced from the backend; this view only lays them out.
  // Refetch on any board mutation (epic moves/links change the layout).
  const queryClient: QueryClient = useQueryClient();
  const roadmapQuery = useQuery({
    queryKey: boardKeys.roadmap(board.id),
    queryFn: async (): Promise<RoadmapData> => {
      const [nextRows, nextLinks]: [RoadmapRow[], IssueLink[]] = await Promise.all([
        BoardApi.getRoadmap(board.id),
        BoardApi.getRoadmapLinks(board.id)
      ]);
      return { rows: nextRows, links: nextLinks };
    }
  });
  const rows: RoadmapRow[] = roadmapQuery.data?.rows ?? [];
  const links: IssueLink[] = roadmapQuery.data?.links ?? [];
  useEffect(() => {
    return BoardService.subscribe((event: BoardChangeEvent) => {
      if (roadmapChanged(event)) {
        void queryClient.invalidateQueries({ queryKey: boardKeys.roadmap(board.id) });
      }
    });
  }, [queryClient, board.id]);

  const epicRows: EpicRow[] = useMemo(() => {
    const needle: string = filter.trim().toLowerCase();
    return rows
      .filter((row: RoadmapRow) => {
        if (!needle) return true;
        return `${row.epic.summary} ${row.epic.key}`.toLowerCase().includes(needle);
      })
      .map((row: RoadmapRow) => ({
        epic: row.epic,
        start: dayjs(row.start),
        end: dayjs(row.end),
        childrenCount: row.childrenCount,
        completedCount: row.completedCount
      }))
      .sort((a: EpicRow, b: EpicRow) => a.start.valueOf() - b.start.valueOf());
  }, [rows, filter]);

  const range: { start: Dayjs; end: Dayjs; unit: Zoom; totalSlots: number } = useMemo(() => {
    let earliest: Dayjs = dayjs();
    let latest: Dayjs = dayjs().add(3, "month");
    for (const row of epicRows) {
      if (row.start.isBefore(earliest)) earliest = row.start;
      if (row.end.isAfter(latest)) latest = row.end;
    }
    earliest = earliest.startOf(zoom === "weeks" ? "week" : "month").subtract(1, zoom === "weeks" ? "week" : "month");
    latest = latest.endOf(zoom === "weeks" ? "week" : "month").add(1, zoom === "weeks" ? "week" : "month");
    const unit: dayjs.ManipulateType = zoom === "weeks" ? "week" : zoom === "quarters" ? "month" : "month";
    const totalSlots: number = Math.max(6, latest.diff(earliest, unit));
    return { start: earliest, end: latest, unit: zoom, totalSlots };
  }, [epicRows, zoom]);

  const headerSlots: Dayjs[] = useMemo(() => {
    const slots: Dayjs[] = [];
    const slotUnit: dayjs.ManipulateType = zoom === "weeks" ? "week" : "month";
    for (let i = 0; i <= range.totalSlots; i += 1) {
      slots.push(range.start.add(i, slotUnit));
    }
    return slots;
  }, [range, zoom]);

  const positionFor = (date: Dayjs): number => {
    const slotUnit: dayjs.ManipulateType = zoom === "weeks" ? "week" : "month";
    const diff: number = date.diff(range.start, slotUnit, true);
    return (diff / range.totalSlots) * 100;
  };

  const widthFor = (start: Dayjs, end: Dayjs): number => {
    const slotUnit: dayjs.ManipulateType = zoom === "weeks" ? "week" : "month";
    const diff: number = end.diff(start, slotUnit, true);
    return Math.max(2, (diff / range.totalSlots) * 100);
  };

  const now: Dayjs = dayjs();
  const nowLeft: number = positionFor(now);
  const isNowVisible: boolean = nowLeft >= 0 && nowLeft <= 100;

  const open = (issue: Issue): void => navigate(`${buildBoardPath(domainName ?? "", board)}/${issue.key}`);

  const slotUnit: dayjs.ManipulateType = zoom === "weeks" ? "week" : "month";

  const beginDrag = (event: React.PointerEvent<HTMLElement>, row: EpicRow, mode: "move" | "resize"): void => {
    event.preventDefault();
    event.stopPropagation();
    const body: HTMLDivElement | null = timelineBodyRef.current;
    if (!body) return;
    const bodyWidth: number = body.getBoundingClientRect().width;
    const startX: number = event.clientX;
    const origStart: Dayjs = row.start;
    const origEnd: Dayjs = row.end;
    draggedRef.current = false;

    const onMove = (moveEvent: PointerEvent): void => {
      const deltaPx: number = moveEvent.clientX - startX;
      if (Math.abs(deltaPx) > 3) draggedRef.current = true;
      const deltaUnits: number = Math.round((deltaPx / bodyWidth) * range.totalSlots);
      if (deltaUnits === 0) return;
      if (mode === "move") {
        const newStart: Dayjs = origStart.add(deltaUnits, slotUnit);
        const newEnd: Dayjs = origEnd.add(deltaUnits, slotUnit);
        BoardService.updateIssue(row.epic.id, {
          startDate: newStart.format("YYYY-MM-DD"),
          dueDate: newEnd.format("YYYY-MM-DD")
        });
      } else {
        const newEnd: Dayjs = origEnd.add(deltaUnits, slotUnit);
        if (newEnd.isAfter(origStart)) {
          BoardService.updateIssue(row.epic.id, { dueDate: newEnd.format("YYYY-MM-DD") });
        }
      }
    };
    const onUp = (): void => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  if (epicRows.length === 0) {
    return (
      <div className="kuika-board-roadmap kuika-board-roadmap--empty">
        <div className="kuika-board-roadmap__empty-card">
          <div className="kuika-board-roadmap__empty-title">No epics yet</div>
          <div className="kuika-board-roadmap__empty-body">
            Create an issue with type <strong>Epic</strong> to plot it on the roadmap. Set its due date to control the
            timeline span.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="kuika-board-roadmap">
      <div className="kuika-board-roadmap__controls">
        <div className="kuika-board-roadmap__zoom">
          {(Object.keys(ZOOM_LABELS) as Zoom[]).map((option: Zoom) => (
            <button
              key={option}
              type="button"
              className={classNames("kuika-board-roadmap__zoom-button", {
                "kuika-board-roadmap__zoom-button--active": zoom === option
              })}
              onClick={() => setZoom(option)}
            >
              {ZOOM_LABELS[option]}
            </button>
          ))}
        </div>
        <span className="kuika-board-roadmap__count">{epicRows.length} epics</span>
      </div>

      <div className="kuika-board-roadmap__grid">
        <div className="kuika-board-roadmap__rail">
          <div className="kuika-board-roadmap__rail-head">Epic</div>
          {epicRows.map((row: EpicRow) => {
            const project: Project | undefined = projects.find((entry: Project) => entry.key === row.epic.projectKey);
            return (
              <div
                key={row.epic.id}
                className={classNames("kuika-board-roadmap__rail-row", OVERFLOW_REVEAL_HOST_CLASS)}
                onClick={() => open(row.epic)}
                role="button"
                tabIndex={0}
              >
                <span
                  className="kuika-board-roadmap__rail-dot"
                  style={{ background: project?.color ?? "var(--purple-5)" }}
                  aria-hidden="true"
                />
                <span className="kuika-board-roadmap__rail-text">
                  <span className="kuika-board-roadmap__rail-key">{row.epic.key}</span>
                  <span className="kuika-board-roadmap__rail-summary">
                    <KuikaOverflowText>{row.epic.summary}</KuikaOverflowText>
                  </span>
                </span>
                {showProject && project && <span className="kuika-board-roadmap__rail-project">{project.key}</span>}
              </div>
            );
          })}
        </div>

        <div className="kuika-board-roadmap__timeline">
          <div className="kuika-board-roadmap__timeline-header">
            {headerSlots.map((slot: Dayjs, index: number) => (
              <div
                key={index}
                className="kuika-board-roadmap__timeline-slot"
                style={{ left: `${(index / range.totalSlots) * 100}%`, width: `${(1 / range.totalSlots) * 100}%` }}
              >
                {zoom === "weeks" ? `W${slot.week()}` : slot.format(zoom === "quarters" ? "[Q]Q YYYY" : "MMM 'YY")}
              </div>
            ))}
          </div>

          <div ref={timelineBodyRef} className="kuika-board-roadmap__timeline-body">
            {isNowVisible && (
              <div className="kuika-board-roadmap__now-line" style={{ left: `${nowLeft}%` }} aria-hidden="true">
                <span className="kuika-board-roadmap__now-pill">Today</span>
              </div>
            )}

            {(() => {
              const geometry: Map<string, BarGeometry> = new Map();
              epicRows.forEach((row: EpicRow, index: number) => {
                const left: number = Math.max(0, positionFor(row.start));
                const width: number = Math.min(100 - left, widthFor(row.start, row.end));
                geometry.set(row.epic.id, { rowIndex: index, left, width });
              });
              const connectors: { id: string; x1: number; y1: number; x2: number; y2: number }[] = [];
              for (const link of links) {
                if (link.type !== "blocks" && link.type !== "blocked_by") continue;
                const fromId: string = link.type === "blocks" ? link.fromIssueId : link.toIssueId;
                const toId: string = link.type === "blocks" ? link.toIssueId : link.fromIssueId;
                const from: BarGeometry | undefined = geometry.get(fromId);
                const to: BarGeometry | undefined = geometry.get(toId);
                if (!from || !to) continue;
                connectors.push({
                  id: link.id,
                  x1: from.left + from.width,
                  y1: from.rowIndex * ROW_HEIGHT + ROW_HEIGHT / 2,
                  x2: to.left,
                  y2: to.rowIndex * ROW_HEIGHT + ROW_HEIGHT / 2
                });
              }
              if (connectors.length === 0) return null;
              return (
                <svg
                  className="kuika-board-roadmap__deps"
                  height={epicRows.length * ROW_HEIGHT}
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  <defs>
                    <marker id="kuika-roadmap-arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                      <path d="M0,0 L6,3 L0,6 Z" fill="var(--dark-5)" />
                    </marker>
                  </defs>
                  {connectors.map((c) => (
                    <line
                      key={c.id}
                      x1={`${c.x1}%`}
                      y1={c.y1}
                      x2={`${c.x2}%`}
                      y2={c.y2}
                      stroke="var(--dark-5)"
                      strokeWidth={1.5}
                      strokeDasharray="4 3"
                      markerEnd="url(#kuika-roadmap-arrow)"
                    />
                  ))}
                </svg>
              );
            })()}

            {epicRows.map((row: EpicRow) => {
              const project: Project | undefined = projects.find((entry: Project) => entry.key === row.epic.projectKey);
              const doneStatusIds: Set<string> = new Set(
                statuses.filter((s: BoardStatus) => s.category === "done").map((s: BoardStatus) => s.id)
              );
              const completed: number = row.completedCount;
              const progress: number = row.childrenCount > 0 ? completed / row.childrenCount : 0;
              const left: number = Math.max(0, positionFor(row.start));
              const width: number = Math.min(100 - left, widthFor(row.start, row.end));
              const isDone: boolean = doneStatusIds.has(row.epic.statusId);
              return (
                <div key={row.epic.id} className="kuika-board-roadmap__timeline-row">
                  <div
                    className={classNames("kuika-board-roadmap__epic-bar", {
                      "kuika-board-roadmap__epic-bar--done": isDone
                    })}
                    style={{
                      left: `${left}%`,
                      width: `${width}%`,
                      background: `linear-gradient(90deg, ${project?.color ?? "var(--purple-5)"} 0%, color-mix(in srgb, ${project?.color ?? "var(--purple-5)"} 50%, var(--dark-0)) 100%)`
                    }}
                    onPointerDown={(event: React.PointerEvent<HTMLDivElement>) => beginDrag(event, row, "move")}
                    onClick={() => {
                      if (draggedRef.current) {
                        draggedRef.current = false;
                        return;
                      }
                      open(row.epic);
                    }}
                    role="button"
                    tabIndex={0}
                    title="Drag to reschedule · drag right edge to resize"
                  >
                    <span className="kuika-board-roadmap__epic-label">{row.epic.summary}</span>
                    {row.childrenCount > 0 && (
                      <span
                        className="kuika-board-roadmap__epic-progress"
                        title={`${completed} / ${row.childrenCount} done`}
                      >
                        <span
                          className="kuika-board-roadmap__epic-progress-fill"
                          style={{ width: `${Math.round(progress * 100)}%` }}
                        />
                      </span>
                    )}
                    <span
                      className="kuika-board-roadmap__epic-resize"
                      onPointerDown={(event: React.PointerEvent<HTMLSpanElement>) => beginDrag(event, row, "resize")}
                      aria-hidden="true"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoardRoadmapView;
